<template>
  <div class="etb-ss-wrap">
    <div v-if="hasBalanceWarning" class="etb-ss-alert no-print">
      <div class="etb-ss-alert__head">
        <feather-icon
          icon="AlertTriangleIcon"
          size="18"
          class="etb-ss-alert__icon"
        />
        <div class="etb-ss-alert__head-text">
          <strong>Balance check</strong>
          <p class="etb-ss-alert__hint mb-0">{{ balanceWarningHint }}</p>
        </div>
      </div>

      <div
        v-for="month in normalizedImbalances"
        :key="month.key"
        class="etb-ss-alert__month"
      >
        <div class="etb-ss-alert__month-head">
          <span class="etb-ss-alert__month-name">{{ month.label }}</span>
          <span v-if="month.difference != null" class="etb-ss-alert__diff">
            Out of balance by {{ formatNet(month.difference) }}
          </span>
        </div>

        <div v-if="month.vouchers.length" class="etb-ss-alert__scroll">
          <table class="etb-ss-vouchers">
            <thead>
              <tr>
                <th>Voucher</th>
                <th>Date</th>
                <th class="is-num">Debit</th>
                <th class="is-num">Credit</th>
                <th class="is-num">Difference</th>
                <th>Affected accounts</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(voucher, vi) in month.vouchers"
                :key="month.key + '-v-' + vi"
              >
                <td class="etb-ss-voucher-no">
                  <span class="etb-ss-voucher-type">
                    {{ voucher.voucherType || "JV" }}
                  </span>
                  #{{
                    voucher.voucherNumStr ||
                    voucher.voucherNum ||
                    voucher.journalEntryID ||
                    "—"
                  }}
                </td>
                <td>{{ formatVoucherDate(voucher.date) }}</td>
                <td class="is-num">{{ formatNet(voucher.debit) }}</td>
                <td class="is-num">{{ formatNet(voucher.credit) }}</td>
                <td class="is-num etb-ss-voucher-diff">
                  {{ formatNet(voucherDifference(voucher)) }}
                </td>
                <td class="etb-ss-voucher-accounts">
                  <div
                    v-for="(account, ai) in voucherAccounts(voucher)"
                    :key="month.key + '-v-' + vi + '-a-' + ai"
                    class="etb-ss-voucher-account"
                  >
                    <span class="etb-ss-voucher-code">
                      {{ account.accountCode }}
                    </span>
                    <span class="etb-ss-voucher-title">
                      {{ account.accountTitle }}
                    </span>
                    <span v-if="account.debit" class="etb-ss-voucher-dr">
                      Dr {{ formatNet(account.debit) }}
                    </span>
                    <span v-if="account.credit" class="etb-ss-voucher-cr">
                      Cr {{ formatNet(account.credit) }}
                    </span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-else class="etb-ss-alert__month-note mb-0">
          <span v-if="month.difference != null">
            Month difference: {{ formatNet(month.difference) }}
          </span>
          <span v-else>Month totals do not balance.</span>
        </p>
      </div>

      <div v-if="hasClosingDifference" class="etb-ss-alert__closing">
        <span>Closing difference</span>
        <strong>{{ formatNet(closingDifference) }}</strong>
      </div>
    </div>

    <div class="etb-ss-scroll">
      <table class="etb-ss-table">
        <thead>
          <tr class="etb-ss-head-1">
            <th rowspan="2" class="etb-ss-th-title">Account title</th>
            <th rowspan="2" class="etb-ss-th-code">Account number</th>
            <th colspan="2" class="etb-ss-th-group etb-ss-th-op">Op. Bal</th>
            <th
              v-for="key in monthKeys"
              :key="'mh-' + key"
              rowspan="2"
              class="etb-ss-th-month"
            >
              {{ key }}
            </th>
            <th colspan="2" class="etb-ss-th-group etb-ss-th-cl">Cl. Bal</th>
          </tr>
          <tr class="etb-ss-head-2">
            <th class="etb-ss-th-drcr etb-ss-th-op">Dr.</th>
            <th class="etb-ss-th-drcr etb-ss-th-op">Cr.</th>
            <th class="etb-ss-th-drcr etb-ss-th-cl">Dr.</th>
            <th class="etb-ss-th-drcr etb-ss-th-cl">Cr.</th>
          </tr>
        </thead>

        <tbody>
          <!-- data is already sorted (sno) — render in order -->
          <tr
            v-for="(item, ri) in orderedItems"
            :key="'r-' + (item.sno != null ? item.sno : ri) + '-' + (item.accountID || ri)"
            class="etb-ss-row"
            :class="rowClass(item)"
          >
            <td class="etb-ss-cell-title" :style="indentStyle(item)">
              <span
                class="etb-ss-title-text"
                :class="{ 'is-parent': !!item.isParent }"
              >
                {{ item.accountTitle }}
              </span>
            </td>
            <td class="etb-ss-cell-code">
              {{ item.accountNumber || item.accountCode || "—" }}
            </td>
            <td class="etb-ss-cell-amt etb-ss-bg-op">
              {{ formatAmount(item.openingDebit) }}
            </td>
            <td class="etb-ss-cell-amt etb-ss-bg-op">
              {{ formatAmount(item.openingCredit) }}
            </td>
            <td
              v-for="key in monthKeys"
              :key="'m-' + ri + '-' + key"
              class="etb-ss-cell-amt etb-ss-cell-month"
              :class="{
                'is-clickable': canOpenLedger(item),
              }"
              :title="canOpenLedger(item) ? 'Open ledger: ' + key : null"
              @click="openLedgerMonth(item, key)"
            >
              {{ formatNet(monthNet(item, key)) }}
            </td>
            <td class="etb-ss-cell-amt etb-ss-bg-cl">
              {{ formatAmount(item.closingDebit) }}
            </td>
            <td class="etb-ss-cell-amt etb-ss-bg-cl">
              {{ formatAmount(item.closingCredit) }}
            </td>
          </tr>

          <!-- sections: TOTAL ASSET / TOTAL EXPENSE / … -->
          <tr
            v-for="(sec, si) in sectionRows"
            :key="'sec-' + si"
            class="etb-ss-section"
          >
            <td class="etb-ss-footer-label" colspan="2">
              {{ sectionLabel(sec) }}
            </td>
            <td class="etb-ss-cell-amt etb-ss-bg-op">
              {{ formatAmount(sec.openingDebit) }}
            </td>
            <td class="etb-ss-cell-amt etb-ss-bg-op">
              {{ formatAmount(sec.openingCredit) }}
            </td>
            <td
              v-for="key in monthKeys"
              :key="'sm-' + si + '-' + key"
              class="etb-ss-cell-amt"
            >
              {{ formatNet(monthNet(sec, key)) }}
            </td>
            <td class="etb-ss-cell-amt etb-ss-bg-cl">
              {{ formatAmount(sec.closingDebit) }}
            </td>
            <td class="etb-ss-cell-amt etb-ss-bg-cl">
              {{ formatAmount(sec.closingCredit) }}
            </td>
          </tr>
        </tbody>

        <tfoot v-if="footerTotal">
          <tr class="etb-ss-grand">
            <td class="etb-ss-footer-label" colspan="2">
              {{ footerTotal.label || "GRAND TOTAL" }}
              <span
                v-if="footerTotal.isBalanced === true"
                class="etb-ss-bal-tag"
              >
                Balanced
              </span>
              <span
                v-else-if="footerTotal.isBalanced === false"
                class="etb-ss-bal-tag is-off"
              >
                Unbalanced
              </span>
            </td>
            <td class="etb-ss-cell-amt">
              {{ formatAmount(footerTotal.openingDebit) }}
            </td>
            <td class="etb-ss-cell-amt">
              {{ formatAmount(footerTotal.openingCredit) }}
            </td>
            <td
              v-for="key in monthKeys"
              :key="'gt-' + key"
              class="etb-ss-cell-amt"
              :class="{ 'is-imbalance': !isNearZero(footerMonth(key)) }"
            >
              {{ formatNet(footerMonth(key)) }}
            </td>
            <td class="etb-ss-cell-amt">
              {{ formatAmount(footerTotal.closingDebit) }}
            </td>
            <td class="etb-ss-cell-amt">
              {{ formatAmount(footerTotal.closingCredit) }}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
</template>

<script>
const MONTH_ABBR = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export default {
  name: "EtbFgsSpreadsheetTable",
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    monthKeys: {
      type: Array,
      default: () => [],
    },
    /** API totals (months should each be 0; closingDifference when unbalanced) */
    totals: {
      type: Object,
      default: null,
    },
    /** TOTAL ASSET / TOTAL EXPENSE / … */
    sections: {
      type: Array,
      default: () => [],
    },
    /** Footer row from API */
    grandTotal: {
      type: Object,
      default: null,
    },
    imbalances: {
      type: Array,
      default: () => [],
    },
    isBalanced: {
      type: Boolean,
      default: null,
    },
  },
  computed: {
    orderedItems() {
      // Already sorted by API (sno) — keep order, do not regroup
      return Array.isArray(this.items) ? this.items : [];
    },
    sectionRows() {
      return Array.isArray(this.sections) ? this.sections : [];
    },
    footerTotal() {
      if (this.grandTotal && typeof this.grandTotal === "object") {
        return {
          ...this.grandTotal,
          isBalanced:
            this.grandTotal.isBalanced != null
              ? !!this.grandTotal.isBalanced
              : this.isBalanced,
        };
      }
      if (this.totals && typeof this.totals === "object") {
        return {
          label: "GRAND TOTAL",
          openingDebit: this.totals.openingDebit,
          openingCredit: this.totals.openingCredit,
          closingDebit: this.totals.closingDebit,
          closingCredit: this.totals.closingCredit,
          months: this.totals.months,
          isBalanced: this.isBalanced,
        };
      }
      return null;
    },
    closingDifference() {
      if (this.grandTotal && this.grandTotal.closingDifference != null) {
        return Number(this.grandTotal.closingDifference);
      }
      if (this.totals && this.totals.closingDifference != null) {
        return Number(this.totals.closingDifference);
      }
      return null;
    },
    imbalanceList() {
      return Array.isArray(this.imbalances) ? this.imbalances : [];
    },
    normalizedImbalances() {
      return this.imbalanceList.map((imb, idx) => {
        const key = `imb-${idx}`;
        if (!imb || typeof imb !== "object") {
          return {
            key,
            label: String(imb || "Unknown month"),
            difference: null,
            vouchers: [],
          };
        }
        const label =
          imb.month || imb.monthKey || imb.key || `Month ${idx + 1}`;
        const rawDiff =
          imb.monthTotal ?? imb.difference ?? imb.net ?? imb.amount;
        const difference = Number(rawDiff);
        return {
          key,
          label: String(label),
          difference: Number.isFinite(difference) ? difference : null,
          vouchers: Array.isArray(imb.unbalancedVouchers)
            ? imb.unbalancedVouchers
            : [],
        };
      });
    },
    hasClosingDifference() {
      return (
        this.closingDifference != null && !this.isNearZero(this.closingDifference)
      );
    },
    hasBalanceWarning() {
      return this.normalizedImbalances.length > 0 || this.hasClosingDifference;
    },
    balanceWarningHint() {
      const months = this.normalizedImbalances.length;
      const vouchers = this.normalizedImbalances.reduce(
        (sum, month) => sum + month.vouchers.length,
        0
      );
      if (vouchers) {
        return `${months} month(s) and ${vouchers} voucher(s) need correction. Fix the debit/credit difference below and regenerate the report.`;
      }
      if (months) {
        return "Month totals do not match. Review the affected vouchers and regenerate the report.";
      }
      return "Closing debit and credit totals do not match.";
    },
  },
  methods: {
    rowClass(item) {
      if (item && item.isParent) return "is-parent-row";
      if (item && Number(item.level) === 1) return "level-1-row";
      if (item && Number(item.level) === 2) return "level-2-row";
      return "item-row";
    },
    indentStyle(item) {
      const level = Number(item && item.level) || 1;
      const pad = (level > 1 ? level - 1 : 0) * 12 + 8;
      return { paddingLeft: pad + "px" };
    },
    sectionLabel(sec) {
      return (
        sec.title ||
        sec.label ||
        sec.name ||
        sec.section ||
        sec.nature ||
        "TOTAL"
      );
    },
    monthNet(item, key) {
      const months = item && item.months;
      if (!months || typeof months !== "object") return 0;
      const n = Number(months[key] ?? months[String(key)] ?? 0);
      return Number.isFinite(n) ? n : 0;
    },
    footerMonth(key) {
      const src = this.footerTotal;
      if (src && src.months && typeof src.months === "object") {
        const n = Number(src.months[key]);
        if (Number.isFinite(n)) return n;
      }
      if (this.totals && this.totals.months) {
        const n = Number(this.totals.months[key]);
        if (Number.isFinite(n)) return n;
      }
      return 0;
    },
    isNearZero(n) {
      return Math.abs(Number(n) || 0) < 0.005;
    },
    formatAmount(value) {
      const num = Number(value);
      if (!Number.isFinite(num) || this.isNearZero(num)) return "-";
      return new Intl.NumberFormat("en-PK", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(num);
    },
    formatNet(value) {
      const n = Number(value);
      if (!Number.isFinite(n) || this.isNearZero(n)) return "0";
      return new Intl.NumberFormat("en-PK", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(n);
    },
    formatVoucherDate(value) {
      if (!value) return "—";
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return String(value);
      return d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },
    voucherDifference(voucher) {
      if (!voucher) return 0;
      const debit = Number(voucher.debit) || 0;
      const credit = Number(voucher.credit) || 0;
      const diff = Number(voucher.difference);
      return Number.isFinite(diff) && diff !== 0 ? diff : debit - credit;
    },
    voucherAccounts(voucher) {
      return voucher && Array.isArray(voucher.accounts)
        ? voucher.accounts
        : [];
    },
    monthKeyToSection(key) {
      const parts = String(key || "").split("-");
      if (parts.length < 2) return null;
      const abbr = parts[0];
      const yy = Number(parts[1]);
      const mi = MONTH_ABBR.findIndex(
        (m) => m.toLowerCase() === String(abbr).toLowerCase()
      );
      if (mi < 0 || !Number.isFinite(yy)) return null;
      const year = yy < 100 ? 2000 + yy : yy;
      const month = mi + 1;
      const fromDate = `${year}-${String(month).padStart(2, "0")}-01`;
      const lastDay = new Date(year, month, 0).getDate();
      const toDate = `${year}-${String(month).padStart(2, "0")}-${String(
        lastDay
      ).padStart(2, "0")}`;
      return { fromDate, toDate, month, year };
    },
    canOpenLedger(item) {
      if (!item) return false;
      if (item.isParent === true) return false;
      if (Number(item.level) === 1) return false;
      return !!(item.accountID || item.accountId);
    },
    openLedgerMonth(item, key) {
      if (!this.canOpenLedger(item)) return;
      const section = this.monthKeyToSection(key);
      if (!section) return;
      this.$emit("open-ledger", item, section);
    },
  },
};
</script>

<style scoped lang="scss">
$border: #c5cdd6;
$header: #1e3a5f;
$text: #2d3436;

.etb-ss-wrap {
  width: 100%;
}

.etb-ss-alert {
  margin-bottom: 0.85rem;
  border: 1px solid #f0cf8a;
  border-radius: 8px;
  background: #fffaf0;
  color: #6b4b00;
  font-size: 0.8rem;

  &__head {
    display: flex;
    align-items: flex-start;
    gap: 0.6rem;
    padding: 0.7rem 0.85rem;
    background: #fff3d6;
    border-bottom: 1px solid #f0cf8a;
  }

  &__icon {
    flex-shrink: 0;
    margin-top: 1px;
    color: #b7791f;
  }

  &__head-text strong {
    font-size: 0.88rem;
    color: #5c4100;
  }

  &__hint {
    margin-top: 0.15rem;
    font-size: 0.74rem;
    color: #8a6d3b;
  }

  &__month {
    padding: 0.65rem 0.85rem;

    & + & {
      border-top: 1px dashed #f0cf8a;
    }
  }

  &__month-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 0.35rem 1rem;
    margin-bottom: 0.45rem;
  }

  &__month-name {
    font-weight: 800;
    font-size: 0.85rem;
    color: #5c4100;
  }

  &__diff {
    font-weight: 700;
    color: #c0392b;
    font-variant-numeric: tabular-nums;
  }

  &__month-note {
    margin-bottom: 0;
    font-size: 0.75rem;
    color: #8a6d3b;
  }

  &__scroll {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  &__closing {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.7rem 0.85rem;
    background: #fdecea;
    border-top: 1px solid #f5c6cb;
    color: #a93226;
    font-weight: 700;

    strong {
      font-size: 1rem;
    }
  }
}

.etb-ss-vouchers {
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  font-size: 0.75rem;
  color: #2d3436;

  th,
  td {
    padding: 0.4rem 0.5rem;
    border: 1px solid #eadfc8;
    text-align: left;
    vertical-align: top;
  }

  th {
    background: #fdf3df;
    color: #6b4b00;
    font-size: 0.68rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    white-space: nowrap;
  }

  .is-num {
    text-align: right;
    white-space: nowrap;
    font-family: "JetBrains Mono", Consolas, monospace;
    font-variant-numeric: tabular-nums;
  }
}

.etb-ss-voucher-no {
  white-space: nowrap;
  font-weight: 700;
}

.etb-ss-voucher-type {
  display: inline-block;
  margin-right: 0.2rem;
  padding: 0.05rem 0.3rem;
  border-radius: 3px;
  background: #e8f0fe;
  color: #1a56b0;
  font-size: 0.64rem;
  font-weight: 700;
}

.etb-ss-voucher-diff {
  color: #c0392b;
  font-weight: 800;
}

.etb-ss-voucher-accounts {
  min-width: 260px;
}

.etb-ss-voucher-account {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 0.2rem 0.4rem;
  padding: 0.15rem 0;

  & + & {
    border-top: 1px dotted #eeeeee;
  }
}

.etb-ss-voucher-code {
  font-family: "JetBrains Mono", Consolas, monospace;
  font-size: 0.64rem;
  color: #8a8f98;
}

.etb-ss-voucher-title {
  flex: 1 1 auto;
  min-width: 120px;
  color: #2d3436;
}

.etb-ss-voucher-dr,
.etb-ss-voucher-cr {
  font-family: "JetBrains Mono", Consolas, monospace;
  font-size: 0.68rem;
  font-variant-numeric: tabular-nums;
  font-weight: 700;
  white-space: nowrap;
}

.etb-ss-voucher-dr {
  color: #1a7f4b;
}

.etb-ss-voucher-cr {
  color: #b03a2e;
}

.etb-ss-scroll {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  border: 1px solid $border;
  border-radius: 6px;
  background: #fff;
}

.etb-ss-table {
  width: max-content;
  min-width: 100%;
  border-collapse: collapse;
  table-layout: auto;
  font-size: 0.72rem;
  color: $text;

  th,
  td {
    border: 1px solid $border;
    padding: 5px 7px;
    vertical-align: middle;
    white-space: nowrap;
  }
}

.etb-ss-head-1,
.etb-ss-head-2 {
  th {
    background: $header;
    color: #fff;
    font-weight: 700;
    text-align: center;
    letter-spacing: 0.02em;
  }
}

.etb-ss-th-title {
  text-align: left !important;
  min-width: 180px;
  position: sticky;
  left: 0;
  z-index: 3;
  background: $header !important;
}

.etb-ss-th-code {
  min-width: 110px;
}

.etb-ss-th-group {
  border-left: 2px solid #fff !important;
  border-right: 2px solid #fff !important;
}

.etb-ss-th-month {
  min-width: 72px;
  border-left: 1px solid rgba(255, 255, 255, 0.25);
}

.etb-ss-th-drcr {
  min-width: 78px;
}

.etb-ss-th-op,
.etb-ss-th-cl {
  background: #254a73 !important;
}

.etb-ss-row {
  &:nth-child(even) td {
    background: #fafbfc;
  }

  &:hover td {
    background: #f3f7fb;
  }

  &.is-parent-row .etb-ss-title-text,
  &.level-1-row .etb-ss-title-text {
    font-weight: 800;
  }

  &.is-parent-row .etb-ss-cell-title,
  &.level-1-row .etb-ss-cell-title {
    background: #f1f3f5 !important;
  }

  &.level-2-row .etb-ss-title-text {
    font-weight: 700;
  }
}

.etb-ss-cell-title {
  text-align: left;
  white-space: normal;
  max-width: 240px;
  position: sticky;
  left: 0;
  z-index: 1;
  background: #fff;
}

.etb-ss-title-text {
  color: #2d3436;
  font-weight: 500;

  &.is-parent {
    font-weight: 800;
  }
}

.etb-ss-cell-code {
  font-family: "JetBrains Mono", Consolas, monospace;
  font-size: 0.65rem;
  color: #636e72;
  text-align: left;
}

.etb-ss-cell-amt {
  text-align: right;
  font-variant-numeric: tabular-nums;
  font-family: "JetBrains Mono", Consolas, monospace;
  font-size: 0.68rem;
}

.etb-ss-cell-month {
  cursor: default;

  &.is-clickable {
    cursor: pointer;

    &:hover {
      background: #e8f4fc !important;
    }
  }

  &.is-imbalance {
    color: #c0392b;
    font-weight: 700;
  }
}

.etb-ss-bg-op {
  background: rgba(9, 132, 227, 0.06);
}

.etb-ss-bg-cl {
  background: rgba(108, 92, 231, 0.06);
}

.etb-ss-section td {
  background: #eef2f6;
  font-weight: 800;
  border-top: 2px solid #9aa8b8;
}

.etb-ss-grand td {
  background: $header;
  color: #fff;
  font-weight: 800;

  &.is-imbalance {
    color: #ffb4b4;
  }
}

.etb-ss-footer-label {
  text-align: left;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.etb-ss-bal-tag {
  display: inline-block;
  margin-left: 0.45rem;
  padding: 0.05rem 0.4rem;
  border-radius: 3px;
  background: rgba(40, 199, 111, 0.25);
  color: #b8f5d0;
  font-size: 0.62rem;
  font-weight: 700;
  text-transform: none;
  letter-spacing: 0;

  &.is-off {
    background: rgba(234, 84, 85, 0.3);
    color: #ffc9c9;
  }
}

@media print {
  .etb-ss-scroll {
    overflow: visible;
    border: 0;
  }

  .etb-ss-table {
    font-size: 6.5pt;
  }

  .etb-ss-cell-month {
    cursor: default;
  }
}
</style>
